/**
 * /oauth/callback
 *
 * The OAuth provider redirects here after the user approves or denies access.
 * URL will look like:
 *   /oauth/callback?code=AUTH_CODE&state=STATE_TOKEN
 *   /oauth/callback?error=access_denied&state=STATE_TOKEN
 *
 * This page handles two scenarios:
 *
 * 1. Opened in a popup (the normal path):
 *    - Sends a postMessage to the opener (the preview page)
 *    - The opener's OAuthConnectButton handles the token exchange
 *    - This window closes itself
 *
 * 2. Same-tab redirect (popup was blocked):
 *    - Stores the code+state in sessionStorage
 *    - Redirects back to the return URL stored before the OAuth redirect
 *    - The OAuthConnectButton on that page reads from sessionStorage and completes
 *
 * This component has NO backend calls — it's purely a relay.
 * All token exchange happens in OAuthConnectButton (or is initiated from there).
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    const isPopup = !!(window.opener && window.opener !== window);

    if (isPopup) {
      // ── Popup path: postMessage back to opener ──────────────────────────
      try {
        window.opener.postMessage(
          {
            type: "OAUTH_CALLBACK",
            code: code || null,
            state: state || null,
            error: error || null,
            errorDescription: errorDescription || null,
          },
          window.location.origin,
        );
      } catch {
        // If postMessage fails (e.g. opener was closed), fall through
      }
      // Close the popup
      window.close();
      return;
    }

    // ── Same-tab path: store in sessionStorage and redirect back ───────────
    if (code && state) {
      sessionStorage.setItem(
        "oauth_callback_data",
        JSON.stringify({ code, state }),
      );
    } else if (error) {
      sessionStorage.setItem(
        "oauth_callback_data",
        JSON.stringify({ error, errorDescription }),
      );
    }

    // Redirect back to the page that initiated the OAuth flow
    const returnTo = sessionStorage.getItem("oauth_return_to");
    sessionStorage.removeItem("oauth_return_to");

    if (returnTo && returnTo.startsWith(window.location.origin)) {
      // Same-origin redirect — safe
      window.location.href = returnTo;
    } else {
      // Fallback to preview page
      navigate("/preview", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center px-6">
        {/* Spinner */}
        <div className="w-12 h-12 rounded-full border-4 border-muted border-t-foreground animate-spin" />
        <p className="text-sm text-muted-foreground">
          Completing authorisation…
        </p>
        <p className="text-xs text-muted-foreground/60">
          This window will close automatically.
        </p>
      </div>
    </div>
  );
}
